import React, { memo, useEffect, useState } from 'react';

import { discountTypes } from 'lib/constants/constants';
import { inRn } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import { MAX_PERCENT_VALUE } from 'components/shared/CurrencyInput/lib/constants';

const InvoiceDiscountCellRenderer = (params) => {
  const { value, node, api, maxValue: rowMaxValue, disabled } = params || {};
  const [currentDiscount, setCurrentDiscount] = useState(value || {});

  useEffect(() => {
    setCurrentDiscount(value || {});
  }, [value]);

  const currentUnit =
    discountTypes?.find((type) => type?.value === currentDiscount?.type) || discountTypes?.[0];
  const invoiceBalanceCents = inRn(node.data?.invoice_balance);
  const rowInitialDiscountCents = inRn(node.data?.row_initial_discount_cents);

  const currentTempId = node?.data?.tempId;

  const countOtherNewDiscounts = () => {
    let otherNewCents = 0;
    if (api && node) {
      api.forEachNode((gridNode) => {
        if (!gridNode.data) return;
        const isSameRow = gridNode === node || gridNode.data?.tempId === currentTempId;
        if (isSameRow) return;
        const current = inRn(gridNode.data.discount?.amount_cents);
        const rowInitial = inRn(gridNode.data?.row_initial_discount_cents);
        otherNewCents += Math.max(0, current - rowInitial);
      });
    }
    return otherNewCents;
  };

  const countOtherFreedDiscounts = () => {
    let otherFreedCents = 0;
    if (api && node) {
      api.forEachNode((gridNode) => {
        if (!gridNode.data) return;
        const isSameRow = gridNode === node || gridNode.data?.tempId === currentTempId;
        if (isSameRow) return;
        const current = inRn(gridNode.data.discount?.amount_cents);
        const rowInitial = inRn(gridNode.data?.row_initial_discount_cents);
        otherFreedCents += Math.max(0, rowInitial - current);
      });
    }
    return otherFreedCents;
  };

  const isPercentUnit = currentUnit?.value === 'percent';

  const otherNewDiscountCents = countOtherNewDiscounts();
  const otherFreedCents = countOtherFreedDiscounts();
  const remainingNewBudgetCents = Math.max(
    0,
    invoiceBalanceCents + otherFreedCents - otherNewDiscountCents
  );

  const currentRowFreedCents = Math.max(
    0,
    rowInitialDiscountCents - inRn(node.data?.discount?.amount_cents)
  );
  const reallocatedFromThisRow = Math.min(currentRowFreedCents, otherNewDiscountCents);

  const maxThisRowCents = Math.min(
    Math.max(0, rowInitialDiscountCents + remainingNewBudgetCents - reallocatedFromThisRow),
    inRn(rowMaxValue)
  );
  const baseAmount = inRn(rowMaxValue);
  const maxPercentForThisRow =
    baseAmount > 0 ? Math.floor((maxThisRowCents / baseAmount) * MAX_PERCENT_VALUE) : 0;

  const handleUnitChange = (unit) => {
    setCurrentDiscount((prev) => {
      if (!prev || typeof prev !== 'object') {
        return { type: unit, amount_cents: 0, percentage: 0 };
      }

      const next = { ...prev, type: unit };

      if (unit === 'percent') {
        const currentAmount = inRn(prev.amount_cents);
        const max = inRn(rowMaxValue);
        const percentage = max > 0 ? Math.round((currentAmount / max) * MAX_PERCENT_VALUE) : 0;
        return { ...next, percentage };
      }

      const currentPercentage = inRn(prev.percentage);
      const max = inRn(rowMaxValue);
      const amount = max > 0 ? Math.round((currentPercentage / MAX_PERCENT_VALUE) * max) : 0;

      return { ...next, amount_cents: amount };
    });
  };

  const handleBlur = (newValue) => {
    const rawPercentage = isPercentUnit ? inRn(newValue) : 0;
    const rawAmountCents = isPercentUnit ? 0 : inRn(newValue);
    const otherNewDiscountCents = countOtherNewDiscounts();
    const otherFreedCents = countOtherFreedDiscounts();

    const remainingNewBudgetCents = Math.max(
      0,
      invoiceBalanceCents + otherFreedCents - otherNewDiscountCents
    );
    const currentRowFreedCents = Math.max(
      0,
      rowInitialDiscountCents - inRn(currentDiscount?.amount_cents)
    );
    const reallocatedFromThisRow = Math.min(currentRowFreedCents, otherNewDiscountCents);
    const remainingCents = Math.min(
      Math.max(0, rowInitialDiscountCents + remainingNewBudgetCents - reallocatedFromThisRow),
      inRn(rowMaxValue)
    );

    if (node && api) {
      let finalAmountCents = 0;
      let finalPercentage = 0;

      if (isPercentUnit) {
        const baseAmount = inRn(rowMaxValue);
        const maxPercentForRemaining =
          baseAmount && baseAmount > 0
            ? Math.floor((remainingCents / baseAmount) * MAX_PERCENT_VALUE)
            : 0;

        finalPercentage = Math.min(rawPercentage, maxPercentForRemaining);
        finalAmountCents =
          baseAmount && baseAmount > 0
            ? Math.round((finalPercentage / MAX_PERCENT_VALUE) * baseAmount)
            : 0;

        if (rawPercentage > finalPercentage) {
          showAlert({
            title: 'Discount exceeds invoice balance',
            message:
              'The total discount across all items cannot be greater than the allowed invoice balance. The value has been adjusted to the maximum allowed.',
            color: 'warning'
          });
        }
      } else {
        finalAmountCents = Math.min(rawAmountCents, remainingCents);
        const baseAmount = inRn(rowMaxValue);
        finalPercentage =
          baseAmount && baseAmount > 0
            ? Math.round((finalAmountCents / baseAmount) * MAX_PERCENT_VALUE)
            : 0;

        if (rawAmountCents > finalAmountCents) {
          showAlert({
            title: 'Discount exceeds invoice balance',
            message:
              'The total discount across all items cannot be greater than the allowed invoice balance. The value has been adjusted to the maximum allowed.',
            color: 'warning'
          });
        }
      }

      const newDiscount = {
        ...currentDiscount,
        amount_cents: finalAmountCents,
        percentage: finalPercentage,
        type: currentUnit?.value || 'amount'
      };

      setCurrentDiscount(newDiscount);
      node.setDataValue('discount', newDiscount);
      if (api) {
        api.refreshCells({ columns: ['discount'], force: true });
      }
    } else {
      let amountCents;
      let percentage;

      if (isPercentUnit) {
        percentage = rawPercentage;
        const baseAmount = inRn(rowMaxValue);

        amountCents =
          baseAmount && baseAmount > 0
            ? Math.round((percentage / MAX_PERCENT_VALUE) * baseAmount)
            : 0;
      } else {
        amountCents = Math.min(rawAmountCents, remainingCents);
        const baseAmount = inRn(rowMaxValue);
        percentage =
          baseAmount && baseAmount > 0
            ? Math.round((amountCents / baseAmount) * MAX_PERCENT_VALUE)
            : 0;
      }

      const newDiscount = {
        ...currentDiscount,
        amount_cents: amountCents,
        percentage,
        type: currentUnit?.value || 'amount'
      };

      setCurrentDiscount(newDiscount);
    }
  };

  const currentValue = isPercentUnit
    ? inRn(currentDiscount?.percentage ?? 0)
    : inRn(currentDiscount?.amount_cents ?? 0);

  return (
    <div className="flex h-full w-full items-center">
      <CurrencyInput
        units={discountTypes}
        currentUnit={currentUnit}
        maxValue={isPercentUnit ? maxPercentForThisRow : maxThisRowCents}
        onUnitChange={handleUnitChange}
        optionPosition="top"
        variant="secondary"
        wrapperClassName="h-[38px] !rounded-none !p-0"
        hideBorder
        value={currentValue}
        onBlur={handleBlur}
        disabled={disabled}
      />
    </div>
  );
};

export default memo(InvoiceDiscountCellRenderer);
